import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import LOGO from "@/assets/logo.png";
import AmbientParticles from "@/components/AmbientParticles";

const TEL = "+36303915392";

const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-anthracite-950"
    >
      {/* Layered backgrounds */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.10),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_50%,rgba(122,46,54,0.18),transparent_45%)]" />
      <div className="absolute inset-0 hero-grain opacity-60" />

      {/* Floating ambient gold particles */}
      <AmbientParticles />

      {/* Decorative thin lines */}
      <div className="absolute top-0 left-1/2 w-px h-32 bg-gradient-to-b from-transparent to-gold-500/40" />
      <div className="absolute bottom-0 left-1/2 w-px h-32 bg-gradient-to-t from-transparent to-gold-500/40" />

      {/* Side editorial labels */}
      <div className="hidden xl:flex absolute left-8 top-1/2 -translate-y-1/2 flex-col items-center gap-6">
        <div className="w-px h-24 bg-gold-500/30" />
        <span className="text-[9px] uppercase tracking-[0.5em] text-cream/40 [writing-mode:vertical-rl] rotate-180">
          Anno · Budapest
        </span>
        <div className="w-px h-24 bg-gold-500/30" />
      </div>
      <div className="hidden xl:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col items-center gap-6">
        <div className="w-px h-24 bg-gold-500/30" />
        <span className="text-[9px] uppercase tracking-[0.5em] text-cream/40 [writing-mode:vertical-rl]">
          Rostás Ibolya · Masszőr
        </span>
        <div className="w-px h-24 bg-gold-500/30" />
      </div>

      {/* Top corner ornaments */}
      <div className="absolute top-28 left-6 md:left-10 lg:left-14 hidden md:flex items-center gap-3">
        <span className="text-[10px] uppercase tracking-[0.4em] text-gold-500/70">N° 01</span>
        <div className="w-12 h-px bg-gold-500/40" />
      </div>
      <div className="absolute top-28 right-6 md:right-10 lg:right-14 hidden md:flex items-center gap-3">
        <div className="w-12 h-px bg-gold-500/40" />
        <span className="text-[10px] uppercase tracking-[0.4em] text-gold-500/70">MMXXVI</span>
      </div>

      <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10 lg:px-14 w-full pt-32 pb-20 md:pt-36 md:pb-24">
        <div className="grid grid-cols-12 gap-6 items-center">
          {/* Logo emerging */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            className="col-span-12 md:col-span-5 flex justify-center md:justify-start"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gold-500/10 blur-3xl scale-110" />
              <img
                src={LOGO}
                alt="NOBLESSE"
                data-testid="hero-logo"
                className="relative w-52 sm:w-72 md:w-full max-w-md h-auto drop-shadow-[0_0_40px_rgba(212,175,55,0.15)]"
              />
            </div>
          </motion.div>

          {/* Typographic statement */}
          <div className="col-span-12 md:col-span-7 md:pl-8 lg:pl-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
              className="flex items-center gap-4 mb-6"
            >
              <span
                data-testid="hero-eyebrow"
                className="text-[10px] sm:text-xs uppercase tracking-[0.45em] text-gold-500 font-light"
              >
                Üdvözlöm a NOBLESSE világában
              </span>
              <div className="flex-1 h-px bg-gradient-to-r from-gold-500/60 to-transparent max-w-[140px]" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.45, ease: "easeOut" }}
              data-testid="hero-title"
              className="font-serif text-cream font-light tracking-[-0.02em] leading-[1.2] sm:leading-[0.98] text-left text-2xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem]"
            >
              <span className="inline sm:block">A nyugalom </span>
              <span className="inline sm:block italic gold-gradient-text sm:mt-1">precíz </span>
              <span className="inline sm:block sm:mt-1">mestersége.</span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.7, ease: "easeOut" }}
              className="mt-10 max-w-md flex items-start gap-5"
            >
              <div className="w-12 h-px bg-gold-500 mt-3 flex-shrink-0" />
              <p
                data-testid="hero-subtitle"
                className="text-sm sm:text-base text-cream/70 leading-relaxed font-light"
              >
                Hiszek abban, hogy a valódi feltöltődés alapja a precíz szakmai
                tudás és a zavartalan nyugalom harmóniája.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.9, ease: "easeOut" }}
              className="mt-10 md:mt-12 flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-8"
            >
              <a
                href={`tel:${TEL}`}
                data-testid="hero-cta-button"
                className="cta-button group inline-flex items-center justify-between gap-6 bg-gold-500 hover:bg-gold-400 text-anthracite-950 px-6 sm:px-8 py-4 sm:py-5 text-[10px] sm:text-xs uppercase tracking-[0.3em] font-medium transition-all duration-300 w-full sm:w-auto sm:min-w-[280px]"
              >
                <span>Időpontot egyeztetek</span>
                <Phone className="w-4 h-4 transition-transform group-hover:rotate-12 flex-shrink-0" strokeWidth={1.8} />
              </a>

              <a
                href={`tel:${TEL}`}
                data-testid="hero-phone-link"
                className="hidden sm:inline-flex group flex-col"
              >
                <span className="text-[10px] uppercase tracking-[0.4em] text-cream/40 mb-1">
                  vagy hívjon közvetlenül
                </span>
                <span className="font-serif text-xl sm:text-2xl text-cream group-hover:text-gold-500 transition-colors tracking-wider">
                  +36 30 391 5392
                </span>
              </a>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
      >
        <span className="text-[9px] uppercase tracking-[0.5em] text-cream/40">Görgessen</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-gold-500/60 to-transparent"
        />
      </motion.div>
    </section>
  );
};

export default Hero;
